import React from 'react';
import MainLayout from 'components/layout/MainLayout';
import BlogArticle from 'components/blog/BlogArticle';
import { ASSET_PREFIX } from 'utils/constants';
import { NextSeo } from 'next-seo';
import RoundedButton from 'components/RoundedButton';
import Separator from 'components/Separator';
import { getCta } from 'utils/common';

const Article = () => {
  const IMAGE_DIRECTORY = `${ASSET_PREFIX}/images/blog/articles/how-to-enjoy-your-holidays-to-the-max`;

  return (
    <MainLayout additionalClassName="blogPost">
      <NextSeo
        title="How to Enjoy your Holidays | Win Epic Prizes | DAYMADE"
        description="Learn the top tips to enjoy your travels to the max. Make your next holiday the best one yet. Enter now for a chance to win your dream holiday."
        openGraph={{
          title: 'How to Enjoy your Holidays | Win Epic Prizes | DAYMADE',
          description:
            'Learn the top tips to enjoy your travels to the max. Make your next holiday the best one yet. Enter now for a chance to win your dream holiday.',
          type: 'article',
          url: 'https://www.daymade.co.uk/blog/how-to-enjoy-your-holidays-to-the-max',
          images: [
            {
              url: `${IMAGE_DIRECTORY}/hero.jpg`,
              alt: 'A group of friends near the ocean on a sunny day.',
            },
          ],
        }}
        // additionalMetaTags={[{
        //   name: 'keywords',
        //   content: 'holiday, travel, trips',
        // }]}
      />

      <BlogArticle
        imageDirectory={IMAGE_DIRECTORY}
        heroAltText="A group of friends near the ocean on a sunny day."
        heroImageYPosition="100%"
      >
        <>
          <h1 style={{ fontSize: '1.6em', lineHeight: '1.4em' }}>
            How to Enjoy your Holidays & Travels to the Max
          </h1>

          <p className="subheading">
            Let’s make your holiday the best one yet.
          </p>

          <div
            className="separator"
            style={{ width: '80%', marginTop: 0 }}
          />

          <p
            style={{
              fontStyle: 'italic',
              textAlign: 'center',
              marginBottom: '2em',
            }}
          >
            by Violet Currey
          </p>

          <p>
            Nothing beats that sweet holiday feeling. You know the one, you
            can’t stop talking about it to everyone you know, and you’re so
            excited that you can’t sleep the night before. You’ve got your bags
            ready, and off you go on your epic adventure. We want to have the
            perfect trip when we’re away, but sometimes, we're too worried about
            this rather than letting our hair down and actually enjoying the
            moment.
          </p>

          <p>
            Every holiday is different. Some holidays are all about sitting back
            and doing absolutely nothing (we don’t blame you!). Others are more
            about using this time to reflect and do some good old-fashioned
            self-care. Maybe you’re craving to get out of your comfort zone and
            try something new, or you’re all about a jammed-packed schedule
            ticking off those bucket list dreams? Whatever your dream getaway
            is; we’ve got some tips to help you enjoy it to the max. Plus, we’ve
            got tons of{' '}
            <a
              href="https://www.daymade.co.uk/prizes/trips-and-stays"
              target="_blank"
              rel="noreferrer"
            >
              holiday prizes to be won at DAYMADE
            </a>{' '}
            so you can make your next trip even more special. Warning; you will
            still suffer from the holiday blues especially after following our
            tips.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary1.jpg`}
            alt="A woman sat on a swing in the jungle"
            className="singleBlogImage"
          />

          <h2>Take a moment to unwind from everyday life:</h2>

          <p>
            This may sound obvious, but hear us out! It’s so common to forget to
            switch off from work, life, and everything in between. Holidays are
            the best time to unplug and feel connected wherever you are in the
            world! Mute those work emails, get off your social media and get
            into some good old relaxing activities. Grab a book, go for a walk
            around the local area or relax in nature to recharge your batteries.
            Disconnecting from technology is the best way to reconnect with
            ourselves and others around us. You’re on holiday, and it’s all
            about ‘me time’. This will look different for everyone and, who
            knows, maybe doing a bungee jump is relaxing for you? (You do you!).
            The most important thing is that you come back from your hols
            feeling refreshed and ready to take on your everyday life. (p.s. If
            you’re reading this on your holiday, carry on but make sure you
            switch off that phone - you’ll thank us later!)
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary2.jpg`}
            alt="A woman doing yoga in the sunset"
            className="singleBlogImage"
          />

          <h2>Be Kind and Polite to Locals; respect their customs:</h2>

          <p>
            Fun fact, it’s considered rude in some countries to turn up early.
            Every country in the world has their customs and ways of doing
            things, so it’s important to respect this when we’re out on our
            travels. Do some research and learn your destination's do’s and
            don’ts. So that you don’t get any surprises or confused faces when
            you’re turning up 30 minutes early for local dinner, it sounds
            simple but be nice to others, especially the locals, and you’re
            guaranteed to get that respect in return. Most locals are super
            welcoming and kind to tourists, so if you get familiar with and join
            in with their ways of living life in their country, you'll have an
            epic time on your holiday. Don't forget to smile, say your ‘p’s and
            ‘q’s, and you’re all set to go.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary3.jpg`}
            alt="Streets of Italy"
            className="singleBlogImage"
          />

          <h2>Be Open-Minded!</h2>

          <p>
            Holidays are a time to connect to others, immerse yourself in their
            culture and try new experiences. Wherever your travels take you to
            the world, it’s important to go there with an open mind and get out
            of your comfort zone. You can do this in tons of ways, from trying
            some new cuisine that makes your taste buds go mad to learning about
            how they celebrate their traditions. You never know what you might
            like until you give it a go. Who knows, maybe you’re a huge fan of
            eating 12 grapes on New Year's Eve in Spain, and you didn’t even
            know it. Travelling is about learning about the world and having fun
            trying new experiences. Trust us; if you go with an open mind,
            you’re bound to have the time of your life on your next adventure.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary4.jpg`}
            alt="Silhouette Photo of Man With Backpack Standing in Seashore during Golden Hour"
            className="singleBlogImage"
          />

          <h2>Have a Rough Plan: but be prepared to ditch it!</h2>

          <p>
            Plans, glorious plans. We love planning our epic holiday itinerary
            with the best activities and the must-sees, but here’s the fun part:
            be prepared to get rid of it completely. It’s great to have a rough
            idea of what you want to see and do on your holidays, but the best
            experiences happen when you least expect them. Take a stroll around
            the city and see where you end up, and if you stumble across
            something you like, well, forget your plans and enjoy it. Being on
            holiday is about living in the moment and enjoying not having to
            check the clock 24/7. So if you’ve got back-to-back plans, leave
            some free time for exploring and wandering around to see where your
            holiday takes you.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary5.jpg`}
            alt="Three men standing at a waterfall"
            className="singleBlogImage"
          />

          <h2>Be Adventurous (but stay true to yourself)</h2>

          <p>
            Bucket lists are ultimately filled with holiday activities, and it’s
            such an exciting time to get those ticked off finally. Swimming with
            sharks, polar bear plunges, climbing the pyramids in Mexico or
            seeing the Grand Canyon, the list goes on... No matter what your
            bucket list dreams are, holidays are the best time to get them
            ticked off. Be adventurous and get planning your epic bucket list
            dreams you’re dying to live out. It’s good to let your wild side
            out, but it’s important to stay true to yourself and be realistic
            about what you actually want to do. if all your pals are scuba
            diving, but you actually just want to chill on the beach and relax,
            then do it! Your holiday is your time to do as you please, so make
            sure you get what you need.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary6.jpg`}
            alt="Woman Standing on Cliff"
            className="singleBlogImage"
          />

          <h2>Don’t forget to have ‘chill days’ to re-energise</h2>

          <p>
            If you’re a fan of jam-packed schedules on your holidays; we hear
            you! You want to get the most out of your trip; sometimes, there’s
            so much to see and do. Hear us out, though. You want to make sure
            you include some ‘chill days’ in your busy schedules. There’s
            nothing worse than returning from your holiday feeling exhausted,
            returning to work and needing another holiday straight away. If
            you’re tight on time, then make sure to take an evening to relax to
            re-energise your mind so you’ll be guaranteed to have a holiday
            glow, even if you are visiting somewhere chilly. You can do this in
            all sorts of ways: relaxing in nature, meeting new people or simply
            spending quality time with our fave people (if they’re on holiday
            with you, of course!). Maybe your whole holiday is all about
            chilling every day, so you've got this one sorted.
          </p>

          <img
            src={`${IMAGE_DIRECTORY}/secondary7.jpg`}
            alt="Colourful hammock with ocean view"
            className="singleBlogImage"
          />

          <Separator style={{ margin: '1em 0 2.5em' }} />

          <p>
            Make sure to pack these tips with you on your next travels, so you
            can have the time of your life and enjoy your holiday to the max. We
            bet you your next trip will be the best one yet.
          </p>

          <p>
            Want to have a holiday of a lifetime? Daymade has many{' '}
            <a
              href="https://www.daymade.co.uk/"
              target="_blank"
              rel="noreferrer"
            >
              prizes to win
            </a>
            ! Enter to win your chance to have the best holiday:
          </p>

          <RoundedButton
            href={getCta()?.url}
            style={{ minWidth: '180px', margin: '1em 0 1.5em' }}
          >
            Enter Now
          </RoundedButton>
        </>
      </BlogArticle>
    </MainLayout>
  );
};

export default Article;
